import {GLOW_THREAD, GLOW_THREAD_TEXTURE_KEYS} from '../configs/glowingThreads';
import {MELEE_TEXTURE_KEYS} from '../configs/meleeAnimation';
import {buildThreadGeometry, resolveThreadPoint} from '../utils/effects/glowThreadGeometry';
import {ensureGlowThreadTextures} from '../utils/effects/glowThreadTextures';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';

const SHADOW_TINT = 0x120904;
const TWO_PI = Math.PI * 2;

/**
 * СВЕТЯЩИЕСЯ НИТИ на поле (см. configs/glowingThreads). Нить — контейнер: снаружи
 * он ведет себя как прежняя графика пути (setAlpha, setDepth, destroy, твин
 * прозрачности), а внутри него полосы и огоньки.
 *
 * Огоньки считаются от часов сцены, а не от момента создания нити: поле
 * пересоздает постоянные нити на каждое обновление стора, и они бегут дальше, а
 * не начинают заново. Разовые нити дополнительно дотягиваются от начала к концу.
 *
 * Контейнер уничтожает его владелец; здесь нить просто перестает обновляться.
 */
export default class GlowingThreads {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {number} [options.linearFilter] — режим фильтрации LINEAR для текстур
   */
  constructor(scene, {linearFilter} = {}) {
    this.scene = scene;
    this.linearFilter = linearFilter;
    this.threads = new Set();
    this.isListening = false;
    this.tick = this.tick.bind(this);
  }

  ensureTextures() {
    ensureGlowThreadTextures(this.scene, {linearFilter: this.linearFilter});
    ensureMeleeTextures(this.scene, {linearFilter: this.linearFilter});
  }

  /**
   * Нить по ломаной
   *
   * @param {Object} params
   * @param {Array<{x: number, y: number}>} params.points
   * @param {Object} params.palette — см. GLOW_THREAD_PALETTES
   * @param {number} params.hexSize
   * @param {number} params.depth
   * @param {number} [params.alpha] — стартовая прозрачность контейнера
   * @param {number} [params.revealMs] — за сколько нить дотянется до конца (0 — сразу)
   * @param {number} [params.revealDelayMs] — когда она начнет тянуться
   * @param {boolean} [params.hasEndNode] — точка на конце нити
   * @param {Object[]} [params.endNodeOverlays] — значки на ней, см. SUPPLY_BLOCKED_NODE_OVERLAYS
   * @returns {Phaser.GameObjects.Container}
   */
  createThread({points, palette, hexSize, depth, alpha = 1, revealMs = 0, revealDelayMs = 0, hasEndNode = true, endNodeOverlays}) {
    this.ensureTextures();
    const geometry = buildThreadGeometry(points);
    const children = [];
    const addImage = (x, y, textureKey, tint, imageAlpha = 1) => {
      const image = this.scene.add.image(x, y, textureKey);
      image.setTint(tint);
      image.setAlpha(imageAlpha);
      children.push(image);
      return image;
    };

    const addStrips = (textureKey, tint, imageAlpha, widthFactor) => geometry.segments.map((segment) => {
      const strip = addImage(segment.x, segment.y, textureKey, tint, imageAlpha);
      strip.setOrigin(0, 0.5);
      strip.setRotation(segment.angle);
      strip.scaleYBase = (hexSize * widthFactor) / (strip.height || 1);
      return {strip, segment};
    });
    // Снизу вверх: темная подложка, ореол, сердцевина. Круги в изломах закрывают
    // зазор сердцевины на углу
    const strips = [
      ...addStrips(GLOW_THREAD_TEXTURE_KEYS.HALO, SHADOW_TINT, palette.shadowAlpha, GLOW_THREAD.shadowWidthFactor),
      ...addStrips(GLOW_THREAD_TEXTURE_KEYS.HALO, palette.halo, palette.haloAlpha, GLOW_THREAD.haloWidthFactor),
      ...addStrips(GLOW_THREAD_TEXTURE_KEYS.CORE, palette.core, 1, GLOW_THREAD.coreWidthFactor),
    ];
    const joints = geometry.segments.slice(1).map((segment) => {
      const joint = addImage(segment.x, segment.y, GLOW_THREAD_TEXTURE_KEYS.NODE, palette.core);
      joint.setScale((hexSize * GLOW_THREAD.coreWidthFactor * 0.62) / (joint.width || 1));
      return {joint, start: segment.start};
    });

    const moteCount = Math.max(
      GLOW_THREAD.minMotes,
      Math.round(geometry.length / (hexSize * GLOW_THREAD.moteSpacingFactor)),
    );
    const moteSizeFactor = palette.moteSizeFactor ?? GLOW_THREAD.moteSizeFactor;
    const motes = Array.from({length: moteCount}, () => {
      const mote = addImage(0, 0, MELEE_TEXTURE_KEYS.GLOW, palette.mote, 0);
      mote.setScale((hexSize * moteSizeFactor) / (mote.width || 1));
      return mote;
    });

    const head = revealMs > 0 ? addImage(0, 0, MELEE_TEXTURE_KEYS.GLOW, palette.mote, 0) : null;
    if (head) {
      head.setScale((hexSize * GLOW_THREAD.headSizeFactor) / (head.width || 1));
    }

    const endPoint = points[points.length - 1];
    const endNode = hasEndNode && endPoint
      ? this.addNodeImages(children, addImage, endPoint, palette, hexSize, GLOW_THREAD.node.radiusFactor, {overlays: endNodeOverlays})
      : null;

    const container = this.scene.add.container(0, 0, children);
    container.setDepth(depth);
    container.setAlpha(alpha);

    const thread = {
      container,
      geometry,
      strips,
      joints,
      motes,
      head,
      endNode,
      hexSize,
      revealMs,
      revealDelayMs,
      createdAt: this.now(),
    };
    this.threads.add(thread);
    this.updateThread(thread, thread.createdAt);
    this.listen();
    return container;
  }

  /**
   * Точки на фермах сети и пути
   *
   * @param {Object} params
   * @param {Array<{x: number, y: number}>} params.points
   * @param {Object} params.palette
   * @param {number} params.hexSize
   * @param {number} params.depth
   * @param {number} [params.alpha]
   * @param {boolean} [params.isSmall] — маленькая точка (ферма-источник)
   * @param {number[]} [params.arrowAngles] — стрелка на точке транзита: угол по маршруту, радианы
   * @param {Object[]} [params.overlays] — значки на всех точках, см. SUPPLY_BLOCKED_NODE_OVERLAYS
   * @returns {?Phaser.GameObjects.Container} null — точек нет
   */
  createNodes({points, palette, hexSize, depth, alpha = 1, isSmall = false, arrowAngles, overlays}) {
    if (points.length === 0) {
      return null;
    }
    this.ensureTextures();
    const children = [];
    const addImage = (x, y, textureKey, tint, imageAlpha = 1) => {
      const image = this.scene.add.image(x, y, textureKey);
      image.setTint(tint);
      image.setAlpha(imageAlpha);
      children.push(image);
      return image;
    };
    const {radiusFactor, smallRadiusFactor} = GLOW_THREAD.node;
    const nodes = points.map((point, index) => (
      this.addNodeImages(children, addImage, point, palette, hexSize, isSmall ? smallRadiusFactor : radiusFactor, {arrowAngle: arrowAngles?.[index], overlays})
    ));
    const container = this.scene.add.container(0, 0, children);
    container.setDepth(depth);
    container.setAlpha(alpha);
    const thread = {container, nodes, createdAt: this.now()};
    this.threads.add(thread);
    this.updateThread(thread, thread.createdAt);
    this.listen();
    return container;
  }

  addNodeImages(children, addImage, point, palette, hexSize, radiusFactor, {arrowAngle, overlays = []} = {}) {
    const {haloFactor, haloAlpha, arrowTint, arrowSizeFactor} = GLOW_THREAD.node;
    const radius = hexSize * radiusFactor;
    const halo = addImage(point.x, point.y, MELEE_TEXTURE_KEYS.GLOW, palette.halo, haloAlpha);
    const shadow = addImage(point.x, point.y, GLOW_THREAD_TEXTURE_KEYS.NODE, SHADOW_TINT, palette.shadowAlpha);
    const core = addImage(point.x, point.y, GLOW_THREAD_TEXTURE_KEYS.NODE, palette.core);
    const arrow = arrowAngle === undefined
      ? null
      : addImage(point.x, point.y, GLOW_THREAD_TEXTURE_KEYS.ARROW, arrowTint);
    arrow?.setRotation(arrowAngle);
    return {
      halo,
      shadow,
      core,
      arrow,
      arrowBase: arrow ? (radius * arrowSizeFactor) / (arrow.width || 1) : 0,
      overlays: overlays.map(({textureKey, tint, sizeFactor, rotation = 0}) => {
        const image = addImage(point.x, point.y, textureKey, tint);
        image.setRotation(rotation);
        return {image, base: (radius * sizeFactor) / (Math.max(image.width, image.height) || 1)};
      }),
      haloBase: (radius * haloFactor * 2) / (halo.width || 1),
      shadowBase: (radius * 2.5) / (shadow.width || 1),
      coreBase: (radius * 2) / (core.width || 1),
    };
  }

  now() {
    return this.scene?.time?.now ?? 0;
  }

  listen() {
    if (!this.isListening && this.scene?.events?.on) {
      this.scene.events.on('update', this.tick);
      this.isListening = true;
    }
  }

  tick() {
    const now = this.now();
    this.threads.forEach((thread) => {
      if (thread.container.active === false) {
        this.threads.delete(thread);
        return;
      }
      this.updateThread(thread, now);
    });
    if (this.threads.size === 0 && this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }

  /** Забыть все нити (разбор поля). Контейнеры уничтожают их владельцы */
  clear() {
    this.threads.clear();
    if (this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }

  updateThread(thread, now) {
    if (thread.nodes) {
      thread.nodes.forEach((node, index) => this.updateNode(node, now, 1, index));
      return;
    }
    const {geometry, strips, joints, motes, head, endNode, hexSize, revealMs, revealDelayMs, createdAt} = thread;
    const share = revealMs > 0 ? Math.min(1, Math.max(0, (now - createdAt - revealDelayMs) / revealMs)) : 1;
    const revealed = geometry.length * share;

    if (thread.lastRevealed !== revealed) {
      thread.lastRevealed = revealed;
      strips.forEach(({strip, segment}) => {
        const visibleLength = Math.min(segment.length, Math.max(0, revealed - segment.start));
        strip.setVisible(visibleLength > 0);
        strip.setScale(visibleLength / (strip.width || 1), strip.scaleYBase);
      });
      joints.forEach(({joint, start}) => joint.setVisible(revealed >= start));
    }

    const periodMs = (geometry.length / (hexSize * GLOW_THREAD.moteSpeedFactor)) * 1000;
    const phase = periodMs > 0 ? now / periodMs : 0;
    motes.forEach((mote, index) => {
      const moteShare = (phase + index / motes.length) % 1;
      const distance = geometry.length * moteShare;
      if (distance > revealed) {
        mote.setAlpha(0);
        return;
      }
      const point = resolveThreadPoint(geometry, distance);
      mote.setPosition(point.x, point.y);
      mote.setAlpha(Math.sin(moteShare * Math.PI));
    });

    if (head) {
      const isMoving = share > 0 && share < 1;
      head.setAlpha(isMoving ? 1 : 0);
      if (isMoving) {
        const point = resolveThreadPoint(geometry, revealed);
        head.setPosition(point.x, point.y);
      }
    }

    if (endNode) {
      const revealEndMs = createdAt + revealDelayMs + revealMs;
      const pop = revealMs > 0 ? Math.min(1, Math.max(0, (now - revealEndMs) / GLOW_THREAD.node.popMs)) : 1;
      this.updateNode(endNode, now, pop, 0);
    }
  }

  updateNode(node, now, pop, index) {
    const {pulseMs, pulseScale} = GLOW_THREAD.node;
    const pulse = 1 + pulseScale * Math.sin((now / pulseMs + index * 0.37) * TWO_PI);
    // Точка выпрыгивает с перехлестом: к концу чуть больше себя и обратно
    const shifted = pop - 1;
    const grow = pop >= 1 ? 1 : 1 + 2.70158 * shifted ** 3 + 1.70158 * shifted ** 2;
    [node.halo, node.shadow, node.core, node.arrow].forEach((image) => image?.setVisible(pop > 0));
    node.halo.setScale(node.haloBase * grow * pulse);
    node.shadow.setScale(node.shadowBase * grow);
    node.core.setScale(node.coreBase * grow);
    node.arrow?.setScale(node.arrowBase * grow);
    node.overlays.forEach(({image, base}) => {
      image.setVisible(pop > 0);
      image.setScale(base * grow);
    });
  }
}
